import { ReactElement } from 'react'
import { useIsOrganisationAdmin } from '@hooks'
import CopyIcon from '@mui/icons-material/ContentCopy'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import Filter2Icon from '@mui/icons-material/Filter2'
import { IconButton, TableCell, TableRow, Tooltip } from '@mui/material'
import type { ProjectShearConnectionUsageDTO, ShearConnectionDTO } from '@api/fastAPI.schemas'
import { AlertDialog } from '@ui/feedback'
import { Box } from '@ui/structure'
import {
  deleteConfirmationText,
  deleteConfirmationTitle,
} from '../../connections/utils/deleteConfirmation'
import { formatShearDistance, formatShearResistance } from '../constants'

interface Props {
  connection: ShearConnectionDTO
  onDelete: () => void
  onEdit: () => void
  onCopy: () => void
  onDuplicate: () => void
  /** Project libraries are editable by every member; only the organisation library is admin-only. */
  isProjectLibrary?: boolean
  usage?: ProjectShearConnectionUsageDTO
  /** Shows the usage column, also while the usage is still loading. */
  showUsage?: boolean
}

const ShearConnectionListItem = ({
  connection,
  onDelete,
  onEdit,
  onCopy,
  onDuplicate,
  isProjectLibrary = false,
  usage,
  showUsage = false,
}: Props): ReactElement => {
  const { name, short_name, guid, export_comment, distance, r_d_wind, r_d_earthquake } = connection
  const { isOrganisationAdmin } = useIsOrganisationAdmin()
  const readOnly = !isProjectLibrary && !isOrganisationAdmin

  return (
    <TableRow
      key={guid}
      sx={({ palette }) => ({
        '&:hover': {
          background: palette.grey[100],
        },
        cursor: 'pointer',
      })}
    >
      <TableCell>{name}</TableCell>
      <TableCell>{short_name}</TableCell>
      <TableCell>{export_comment}</TableCell>
      <TableCell>{formatShearDistance(distance)}</TableCell>
      <TableCell>{formatShearResistance(r_d_earthquake)}</TableCell>
      <TableCell>{formatShearResistance(r_d_wind)}</TableCell>
      {showUsage && (
        <TableCell data-cy="shear-connection-usage">
          {/* The anchor count needs computable checks; the manual count is always known. */}
          {usage ? `${usage.anchorCount ?? '–'} (${usage.userDefinedCount})` : '–'}
        </TableCell>
      )}
      <TableCell align="right">
        <Box display="flex" justifyContent="flex-end">
          {!readOnly && (
            <AlertDialog
              title="Schubverbinder verändern?"
              text={
                isProjectLibrary
                  ? 'Änderungen an diesem Schubverbinder werden nur innerhalb des Projektes angewandt und wirken sich nicht auf andere Projekte aus.'
                  : 'Die Änderung am Schubverbinder wird nicht in bestehende Projekte übernommen. Die Schubverbinder müssen <b>jeweils</b> in den entsprechenden Projekten im Systemmanager geändert werden.'
              }
              onConfirm={onEdit}
            >
              <IconButton size="small">
                <EditIcon fontSize="small" />
              </IconButton>
            </AlertDialog>
          )}
          {!readOnly && (
            <Tooltip title="Duplizieren" enterDelay={500}>
              <IconButton size="small" onClick={onDuplicate} data-cy="duplicate-shear-connection">
                <Filter2Icon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="In Zwischenablage kopieren" enterDelay={500}>
            <IconButton size="small" onClick={onCopy} data-cy="copy-shear-connection">
              <CopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {!readOnly && (
            <AlertDialog
              title={deleteConfirmationTitle(usage, 'shear')}
              text={deleteConfirmationText(usage, 'shear')}
              onConfirm={async () => onDelete()}
            >
              <IconButton size="small">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </AlertDialog>
          )}
        </Box>
      </TableCell>
    </TableRow>
  )
}

export default ShearConnectionListItem
